import { useState } from 'react';
import type { TaskType } from '@api';
import {
  AddActionIcon,
  parseHypeItErrorMessage,
  TaskDetailsModal,
  TaskDetailsModalModeType,
  taskFormDefaultValues,
  TaskFormModal,
  TaskFormSchemaType,
} from '@common';
import { faTable } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { usePermissionContext } from '@hypeit-gmbh/hypeit-ui-core';
import { ActionIcon, Box, Group, Stack, Text, Tooltip } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { Link, useRouterState } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useSubmitUpdateTask } from '../common';
import { TaskItem } from './components';
import {
  useClaimTask,
  useMarkTaskAsRead,
  useUnclaimTask,
  useSubmitCreateTask,
  useTasksData,
  useTaskWebSocket,
  useUpdateTaskStatus,
} from './hooks';

export const TouringTasks: React.FC = () => {
  const { t } = useTranslation('tasks');
  const { hasPermission } = usePermissionContext();
  const [createOpened, { open: openCreate, close: closeCreate }] =
    useDisclosure(false);
  const [detailsOpened, { open: openDetails, close: closeDetails }] =
    useDisclosure(false);
  const [selectedTask, setSelectedTask] = useState<TaskType | null>(null);
  const [mode, setMode] = useState<TaskDetailsModalModeType>('display');
  const defaultContext = useRouterState({
    select: (state) => state.location.pathname,
  });

  const { userData, tasks, tasksError, assignableUsers, assignableTeams } =
    useTasksData();
  const { markTaskAsRead } = useMarkTaskAsRead();
  const { claimTask } = useClaimTask({ currentUser: userData });
  const { unclaimTask } = useUnclaimTask();
  const { closeTask, reopenTask } = useUpdateTaskStatus();
  const { submitCreateTask } = useSubmitCreateTask();
  const { submitUpdateTask } = useSubmitUpdateTask();
  useTaskWebSocket();

  const userCanReadTasks = hasPermission({
    permission: 'TASKS',
    action: 'READ',
  });
  const userCanWriteTasks = hasPermission({
    permission: 'TASKS',
    action: 'WRITE',
  });

  const handleView = (task: TaskType): void => {
    setSelectedTask(task);
    setMode('display');
    openDetails();
  };

  const handleCloseDetails = (): void => {
    setSelectedTask(null);
    setMode('display');
    closeDetails();
  };

  const handleSubmit = async (data: TaskFormSchemaType): Promise<void> => {
    if (selectedTask === null) return;

    const updatedTask = await submitUpdateTask(selectedTask.id, data);
    setSelectedTask(updatedTask);
    setMode('display');
  };

  if (!userCanReadTasks) return null;

  return (
    <Box p="md" mih="100%">
      <Group
        justify="space-between"
        align="center"
        pb="sm"
        mb="md"
        style={{ borderBottom: '1px solid var(--mantine-color-layout-11)' }}
      >
        <Text
          c="text.4"
          size="xs"
          fw={600}
          tt="uppercase"
          style={{ letterSpacing: '0.04em' }}
        >
          {t('TITLE')}
        </Text>
        <Group gap="xs">
          {!tasksError && (
            <>
              <Tooltip label={t('ACTIONS.VIEW_ALL')}>
                <ActionIcon
                  component={Link}
                  to="/tasks"
                  size="sm"
                  color="blue"
                  variant="outline"
                  aria-label={t('ACTIONS.VIEW_ALL')}
                >
                  <FontAwesomeIcon icon={faTable} />
                </ActionIcon>
              </Tooltip>
              {userCanWriteTasks && (
                <Tooltip label={t('ACTIONS.CREATE')}>
                  <AddActionIcon
                    size="sm"
                    onClick={openCreate}
                    ariaLabel={t('ACTIONS.CREATE')}
                  />
                </Tooltip>
              )}
            </>
          )}
        </Group>
      </Group>
      {tasksError && (
        <Text c="text.5" size="sm">
          {parseHypeItErrorMessage(tasksError)}
        </Text>
      )}
      {tasks?.length === 0 && (
        <Text c="text.5" size="sm">
          {t('EMPTY')}
        </Text>
      )}
      <Stack gap="xs">
        {tasks?.map((task) => (
          <TaskItem
            key={task.id}
            task={task}
            currentUserId={userData.id}
            onMarkAsRead={markTaskAsRead}
            onClaim={claimTask}
            onUnclaim={unclaimTask}
            onClose={closeTask}
            onReopen={reopenTask}
            onView={handleView}
          />
        ))}
      </Stack>

      <TaskFormModal
        opened={createOpened}
        onClose={closeCreate}
        onSubmit={submitCreateTask}
        defaultValues={{ ...taskFormDefaultValues, context: defaultContext }}
        title={t('CREATE.TITLE')}
        users={assignableUsers ?? []}
        teams={assignableTeams ?? []}
      />
      {userCanWriteTasks ? (
        <TaskDetailsModal
          opened={detailsOpened}
          onClose={handleCloseDetails}
          task={selectedTask}
          canEdit
          mode={mode}
          onModeChange={setMode}
          onSubmit={handleSubmit}
          users={assignableUsers ?? []}
          teams={assignableTeams ?? []}
        />
      ) : (
        <TaskDetailsModal
          opened={detailsOpened}
          onClose={handleCloseDetails}
          task={selectedTask}
        />
      )}
    </Box>
  );
};
